import { Component, OnInit } from '@angular/core';
import { rosterProfileIconUrl } from '../../utils/roster-image';
import { genreColorClass } from '../../utils/genre-colors';
import { brandLogoUrl } from '../../utils/brand-logo';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RosterService } from '../../services/roster.service';
import { LoginService } from '../../services/login.service';
import { MediaKitResponse } from '../../models/media-kit-response';
import { ProfileImagePipe } from '../../pipes/profileImagePipe';

export interface PlatformProfileStats {
  handle: string;
  profileUrl: string | null;
  followersCount: string;
  followersCountLabel: string;
  totalLikes: string;
  totalLikesLabel: string;
  postsCount: string;
  postsCountLabel: string;
}

export interface PlatformChart {
  platform: string;
  lastRefresh: Date | null;
  profile: PlatformProfileStats | null;
  demographicsNote: string | null;
  hasGenderData: boolean;
  hasCountryData: boolean;
  hasCityData: boolean;
  hasAgeData: boolean;
  genderChartBackground: string;
  femaleDisplay: number;
  maleDisplay: number;
  otherDisplay: number;
  topCountrySlices: Array<{ label: string; percent: number; color: string }>;
  countryChartBackground: string;
  topCitySlices: Array<{ label: string; percent: number; color: string }>;
  cityChartBackground: string;
  ageSlices: Array<{ label: string; percent: number; color: string }>;
  ageChartBackground: string;
}

@Component({
  selector: 'app-media-kit',
  imports: [CommonModule, ProfileImagePipe],
  templateUrl: './media-kit.component.html',
  styleUrl: './media-kit.component.css'
})
export class MediaKitComponent implements OnInit {
  rosterId!: string;
  roster: MediaKitResponse | null = null;
  demographicsList: any[] = [];
  oauthAccounts: any[] = [];
  topVideos: any[] = [];
  pastPartnershipsList: string[] = [];
  loading: boolean = true;
  profileIconUrl = rosterProfileIconUrl;
  error: string = '';
  selectedPlatform: string = '';
  selectedVideosPlatform: string = '';
  refreshing: boolean = false;
  refreshError: string = '';
  headerImageError: boolean = false;
  private _carouselReady: boolean = false;

  get headerImageSrc(): string {
    return '/' + (this.rosterId || '').substring(0, 8) + '.webp';
  }

  onHeaderImageError(): void {
    this.headerImageError = true;
  }

  private readonly countryColors: string[] = [
    '#0ea5e9', '#22c55e', '#f97316', '#a855f7', '#eab308', '#14b8a6', '#ef4444'
  ];
  private readonly cityColors: string[] = [
    '#06b6d4', '#10b981', '#f43f5e', '#8b5cf6', '#f97316', '#facc15', '#64748b'
  ];
  private readonly ageColors: string[] = [
    '#1d4ed8', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444'
  ];

  // ── Computed properties ────────────────────────────────────────────────────

  private readonly platformOrder: string[] = ['instagram', 'tiktok', 'youtube'];

  get platformCharts(): PlatformChart[] {
    const charts = this.demographicsList.map(d => this.buildChart(d));
    return charts.sort((a, b) => {
      const ai = this.platformOrder.indexOf(a.platform.toLowerCase());
      const bi = this.platformOrder.indexOf(b.platform.toLowerCase());
      const aOrder = ai === -1 ? 999 : ai;
      const bOrder = bi === -1 ? 999 : bi;
      return aOrder - bOrder;
    });
  }

  get locationDisplay(): string | null {
    const city = this.roster?.city;
    const state = this.roster?.state;
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return null;
  }

  get audienceDisplay(): string {
    if (this.demographicsList.length === 0) return 'Unknown';
    const total = this.demographicsList.reduce((sum, d) => {
      const count = Number(d?.profile?.followersCount ?? d?.profile?.FollowersCount ?? 0);
      return sum + (Number.isFinite(count) ? count : 0);
    }, 0);
    if (total === 0) return 'Unknown';
    return this.formatFollowers(total) + ' followers';
  }

  get genreTags(): string[] {
    if (!this.roster?.genre) return [];
    return this.roster.genre.split('\n').map(g => g.trim()).filter(g => g.length > 0);
  }

  genreColorClass = genreColorClass;
  brandLogoUrl = brandLogoUrl;

  // Stubbed engagement stats — will be wired once the API provides per-post data
  get igLikes(): string    { return '128k'; }
  get igComments(): string { return '4.5k'; }
  get igShares(): string   { return '7.6k'; }

  get carouselTransform(): string {
    const count = this.platformCharts.length;
    const idx = this.platformCharts.findIndex(
      c => c.platform.toLowerCase() === this.selectedPlatform
    );
    // translateX % is relative to the inner element itself.
    // Each slide occupies (100% / count) of the inner, so offset per slide = (100 / count)%.
    const offset = count > 0 ? (Math.max(0, idx) * 100) / count : 0;
    return `translateX(-${offset}%)`;
  }

  get slideWidth(): string {
    const count = this.platformCharts.length;
    return count > 0 ? `calc(100% / ${count})` : '100%';
  }

  get carouselTransition(): string {
    return this._carouselReady ? 'transform 0.75s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
  }

  get carouselWidth(): string {
    const count = this.platformCharts.length;
    return count > 0 ? `${count * 100}%` : '100%';
  }

  get videoPlatforms(): string[] {
    const found = new Set(this.topVideos.map(v => (v.platform ?? v.Platform ?? '').toLowerCase()));
    return this.platformOrder.filter(p => found.has(p));
  }

  hasVideosPlatform(platform: string): boolean {
    return this.topVideos.some(v => (v.platform ?? v.Platform ?? '').toLowerCase() === platform);
  }

  selectVideosPlatform(platform: string): void {
    this.selectedVideosPlatform = platform.toLowerCase();
  }

  getVideosForPlatform(platform: string): any[] {
    return this.topVideos.filter(v => (v.platform ?? v.Platform ?? '').toLowerCase() === platform);
  }

  videoThumb(video: any): string {
    return video?.thumbnailUrl ?? video?.ThumbnailUrl ?? '';
  }

  // True when there is no usable thumbnail — either none was stored, or the
  // stored URL failed to load (e.g. an expired platform CDN link that could
  // not be refreshed). Drives the placeholder shown in place of the image.
  showThumbFallback(video: any): boolean {
    return !this.videoThumb(video) || !!video?._thumbError;
  }

  onThumbError(video: any): void {
    if (video) video._thumbError = true;
  }

  get videosCarouselTransform(): string {
    const count = this.videoPlatforms.length;
    const idx = this.videoPlatforms.indexOf(this.selectedVideosPlatform);
    const offset = count > 0 ? (Math.max(0, idx) * 100) / count : 0;
    return `translateX(-${offset}%)`;
  }

  get videosSlideWidth(): string {
    const count = this.videoPlatforms.length;
    return count > 0 ? `calc(100% / ${count})` : '100%';
  }

  get videosCarouselWidth(): string {
    const count = this.videoPlatforms.length;
    return count > 0 ? `${count * 100}%` : '100%';
  }

  get videosCarouselTransition(): string {
    return this._carouselReady ? 'transform 0.75s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  get isLoggedIn(): boolean {
    return !!this.loginService.accessToken || !!localStorage.getItem('jwt');
  }

  constructor(
    private route: ActivatedRoute,
    private rosterService: RosterService,
    private loginService: LoginService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.rosterId = params['id'];
      this.loadData();
    });
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  hasPlatform(platform: string): boolean {
    return this.platformCharts.some(c => c.platform.toLowerCase() === platform.toLowerCase());
  }

  selectPlatform(platform: string): void {
    this.selectedPlatform = platform.toLowerCase();
  }

  getHandleDisplay(platform: string): string {
    const p = platform.toLowerCase();
    const useAt = p !== 'instagram';

    // 1. demographics.Profile.Handle — canonical handle stored at OAuth connect time
    const demo = this.demographicsList.find((d: any) => (d.platform ?? '').toLowerCase() === p);
    const demoHandle = (demo?.profile?.Handle ?? demo?.profile?.handle ?? '').toString().trim().replace(/^@+/, '');
    if (demoHandle) return useAt ? '@' + demoHandle : demoHandle;

    // 2. roster.{platform}Handle
    let rosterHandle: string | undefined;
    if (p === 'tiktok')     rosterHandle = this.roster?.tiktokHandle;
    if (p === 'instagram')  rosterHandle = this.roster?.instagramHandle;
    if (p === 'youtube')    rosterHandle = this.roster?.youtubeHandle;
    const cleaned = rosterHandle?.trim().replace(/^@+/, '');
    if (cleaned) return useAt ? '@' + cleaned : cleaned;

    return 'Missing';
  }

  getHandleUrl(platform: string): string | null {
    const p = platform.toLowerCase();

    // Prefer ProfileUrl from demographics (set by the backend provider)
    const demo = this.demographicsList.find((d: any) => (d.platform ?? '').toLowerCase() === p);
    const profileUrl = demo?.profile?.ProfileUrl ?? demo?.profile?.profileUrl;
    if (profileUrl) return profileUrl;

    // Fall back to constructing a URL from the roster handle
    let handle: string | undefined;
    if (p === 'tiktok')     handle = this.roster?.tiktokHandle;
    if (p === 'instagram')  handle = this.roster?.instagramHandle;
    if (p === 'youtube')    handle = this.roster?.youtubeHandle;
    const cleaned = handle?.trim().replace(/^@+/, '');
    if (!cleaned) return null;

    if (p === 'instagram') return `https://www.instagram.com/${cleaned}`;
    if (p === 'tiktok')    return `https://www.tiktok.com/@${cleaned}`;
    if (p === 'youtube')   return `https://www.youtube.com/@${cleaned}`;
    return null;
  }

  formatViews(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm';
    if (n >= 1_000) return Math.round(n / 1_000) + 'k';
    return n.toString();
  }

  loadData(): void {
    this.loading = true;
    this.error = '';
    this._carouselReady = false;

    forkJoin({
      mediaKit: this.rosterService.getMediaKit(this.rosterId).pipe(catchError(() => of(null))),
      partnerships: this.rosterService.getPastPartnerships(this.rosterId).pipe(catchError(() => of([] as string[])))
    }).subscribe({
      next: ({ mediaKit: response, partnerships }) => {
        if (!response) {
          this.error = 'Failed to load media kit data';
          this.loading = false;
          return;
        }
        this.roster = response;
        this.oauthAccounts = response.connectedPlatforms.map(p => ({ platform: p }));
        this.demographicsList = (response.demographics ?? []).map((item: any) => this.normalizeItem(item));
        this.topVideos = response.topVideos?.data ?? [];
        this.pastPartnershipsList = partnerships ?? [];
        if (this.platformCharts.length > 0) {
          this.selectedPlatform = this.platformCharts[0].platform.toLowerCase();
        }
        if (this.videoPlatforms.length > 0) {
          this.selectedVideosPlatform = this.videoPlatforms[0];
        }
        this.loading = false;
        // Enable smooth transitions after the first render settles
        setTimeout(() => { this._carouselReady = true; }, 60);
      },
      error: (err) => {
        console.error('Error loading media kit:', err);
        this.error = 'Failed to load media kit data';
        this.loading = false;
      }
    });
  }


  refreshData(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.refreshError = '';
    this.rosterService.getMediaKit(this.rosterId, true).pipe(catchError(() => of(null))).subscribe({
      next: (response) => {
        if (response) {
          this.demographicsList = (response.demographics ?? []).map((item: any) => this.normalizeItem(item));
          this.topVideos = response.topVideos?.data ?? [];
          if (this.videoPlatforms.length > 0 && !this.videoPlatforms.includes(this.selectedVideosPlatform)) {
            this.selectedVideosPlatform = this.videoPlatforms[0];
          }
        } else {
          this.refreshError = 'Failed to refresh data';
        }
        this.refreshing = false;
      },
      error: () => {
        this.refreshError = 'Failed to refresh data';
        this.refreshing = false;
      }
    });
  }

  get lastRefreshDisplay(): string {
    const chart = this.platformCharts.find(
      c => c.platform.toLowerCase() === this.selectedPlatform);
    if (!chart?.lastRefresh) return '';
    return chart.lastRefresh.toLocaleDateString('en-US',
      { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ── Normalisation ──────────────────────────────────────────────────────────

  private normalizeItem(item: any): any {
    const genders: any[] = (item?.audienceGenders ?? item?.audience_genders ?? []).map((g: any) => ({
      gender: g.gender ?? g.Gender ?? 'Other',
      percentage: this.normalizePercent(g.percentage ?? g.Percentage)
    }));

    const countries: any[] = (item?.audienceCountries ?? item?.audience_countries ?? []).map((c: any) => ({
      country: c.country ?? c.Country ?? '',
      percentage: this.normalizePercent(c.percentage ?? c.Percentage)
    }));

    const cities: any[] = (item?.audienceCities ?? item?.audience_cities ?? []).map((c: any) => ({
      city: c.city ?? c.City ?? '',
      percentage: this.normalizePercent(c.percentage ?? c.Percentage)
    }));

    const ages: any[] = (item?.audienceAges ?? item?.audience_ages ?? []).map((a: any) => ({
      age: a.age ?? a.Age ?? a.ageRange ?? a.AgeRange ?? '',
      percentage: this.normalizePercent(a.percentage ?? a.Percentage)
    }));

    const rawProfile = item?.profile ?? item?.Profile ?? null;
    const lastRefresh = item?.lastRefresh ?? item?.LastRefresh ?? null;
    const demographicsNote = item?.demographicsNote ?? item?.DemographicsNote ?? null;

    return {
      platform: (item?.platform ?? item?.Platform ?? 'Unknown').toString(),
      lastRefresh,
      profile: rawProfile,
      demographicsNote,
      audience_genders: genders,
      audience_countries: countries,
      audience_cities: cities,
      audience_ages: ages
    };
  }

  // ── Chart building ─────────────────────────────────────────────────────────

  private buildChart(demographics: any): PlatformChart {
    const countrySlices = this.buildCountrySlices(demographics);
    const citySlices    = this.buildCitySlices(demographics);
    const ageSlices     = this.buildAgeSlices(demographics);
    const hasGenderData  = Array.isArray(demographics?.audience_genders)  && demographics.audience_genders.length > 0;
    const hasCountryData = Array.isArray(demographics?.audience_countries) && demographics.audience_countries.length > 0;
    const hasCityData    = Array.isArray(demographics?.audience_cities)    && demographics.audience_cities.length > 0;
    const hasAgeData     = Array.isArray(demographics?.audience_ages)      && demographics.audience_ages.length > 0;

    const female = this.getGenderPercent(demographics, 'Female');
    const male   = this.getGenderPercent(demographics, 'Male');
    // Only compute residual Other when there is actual gender data; avoid a phantom 100% Other slice.
    const other  = hasGenderData ? Math.max(0, 1 - female - male) : 0;
    const genders: Array<{ label: string; percent: number; color: string }> = [
      { label: 'Female', percent: female, color: '#f59e0b' },
      { label: 'Male',   percent: male,   color: '#2563eb' },
      { label: 'Other',  percent: other,  color: '#6b7280' }
    ].filter(s => s.percent > 0);

    const rawProfile = demographics?.profile ?? null;
    const statLabels = rawProfile?.statLabels ?? rawProfile?.StatLabels ?? {};
    const profile: PlatformProfileStats | null = rawProfile ? {
      handle:              (rawProfile.handle ?? rawProfile.Handle ?? '').toString().replace(/^@+/, ''),
      profileUrl:          rawProfile.profileUrl ?? rawProfile.ProfileUrl ?? null,
      followersCount:      this.formatFollowers(Number(rawProfile.followersCount ?? rawProfile.FollowersCount ?? 0)),
      followersCountLabel: statLabels['followersCount'] ?? 'Followers',
      totalLikes:          this.formatFollowers(Number(rawProfile.totalLikes ?? rawProfile.TotalLikes ?? 0)),
      totalLikesLabel:     statLabels['totalLikes'] ?? 'Total Likes',
      postsCount:          (Number(rawProfile.postsCount ?? rawProfile.PostsCount ?? 0)).toLocaleString(),
      postsCountLabel:     statLabels['postsCount'] ?? 'Posts'
    } : null;

    return {
      platform: demographics.platform,
      lastRefresh: demographics.lastRefresh ? new Date(demographics.lastRefresh) : null,
      profile,
      demographicsNote: demographics.demographicsNote ?? null,
      hasGenderData,
      hasCountryData,
      hasCityData,
      hasAgeData,
      genderChartBackground:  this.slicesToConic(genders),
      femaleDisplay: Math.round(female * 100),
      maleDisplay:   Math.round(male   * 100),
      otherDisplay:  Math.round(other  * 100),
      topCountrySlices: countrySlices,
      countryChartBackground: this.slicesToConic(countrySlices),
      topCitySlices: citySlices,
      cityChartBackground:    this.slicesToConic(citySlices),
      ageSlices,
      ageChartBackground:     this.slicesToConic(ageSlices)
    };
  }

  private buildCountrySlices(demographics: any): Array<{ label: string; percent: number; color: string }> {
    const countries: any[] = Array.isArray(demographics?.audience_countries) ? demographics.audience_countries : [];
    return this.buildTopSlices(
      countries.map(c => ({ label: (c?.country ?? '').toString().trim(), percent: this.normalizePercent(c?.percentage) })),
      3, this.countryColors
    );
  }

  private buildCitySlices(demographics: any): Array<{ label: string; percent: number; color: string }> {
    const cities: any[] = Array.isArray(demographics?.audience_cities) ? demographics.audience_cities : [];
    return this.buildTopSlices(
      cities.map(c => ({ label: this.abbreviateCity((c?.city ?? '').toString().trim()), percent: this.normalizePercent(c?.percentage) })),
      3, this.cityColors
    );
  }

  private readonly stateAbbreviations: Record<string, string> = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
    'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
    'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
  };

  /** Replace full US state name in a city string with its 2-letter abbreviation.
   *  e.g. "Seattle, Washington" → "Seattle, WA" */
  private abbreviateCity(city: string): string {
    for (const [full, abbr] of Object.entries(this.stateAbbreviations)) {
      // Match ", StateName" at end of string (case-insensitive)
      const regex = new RegExp(`,\\s*${full}\\s*$`, 'i');
      if (regex.test(city)) {
        return city.replace(regex, `, ${abbr}`);
      }
    }
    return city;
  }

  private buildAgeSlices(demographics: any): Array<{ label: string; percent: number; color: string }> {
    const ages: any[] = Array.isArray(demographics?.audience_ages) ? demographics.audience_ages : [];
    const normalized = ages
      .map(a => ({ label: (a?.age ?? '').toString().trim(), percent: this.normalizePercent(a?.percentage) }))
      .filter(a => a.label.length > 0 && a.percent > 0);
    normalized.sort((a, b) => b.percent - a.percent);
    return normalized.slice(0, 3).map((item, i) => ({ ...item, color: this.ageColors[i % this.ageColors.length] }));
  }

  private buildTopSlices(
    items: Array<{ label: string; percent: number }>,
    max: number,
    colors: string[]
  ): Array<{ label: string; percent: number; color: string }> {
    const filtered = items.filter(i => i.label.length > 0 && i.percent > 0);
    const isOther = (v: string) => v.toLowerCase() === 'others' || v.toLowerCase() === 'other';
    const others  = filtered.filter(i => isOther(i.label));
    const main    = filtered.filter(i => !isOther(i.label));
    main.sort((a, b) => b.percent - a.percent);
    const top = main.slice(0, max);
    const remaining = main.slice(max);
    const otherPercent = [...others, ...remaining].reduce((sum, i) => sum + i.percent, 0);
    const withOthers = otherPercent > 0 ? [...top, { label: 'Others', percent: otherPercent }] : top;
    return withOthers.map((item, i) => ({ ...item, color: colors[i % colors.length] }));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private slicesToConic(slices: Array<{ percent: number; color: string }>): string {
    if (!slices.length) return 'conic-gradient(#d1d5db 0 360deg)';
    const total = slices.reduce((s, i) => s + i.percent, 0);
    const safeTotal = total > 0 ? total : 1;
    let start = 0;
    const segments = slices.map(slice => {
      const end = start + (Math.max(0, slice.percent) / safeTotal) * 360;
      const seg = `${slice.color} ${start}deg ${end}deg`;
      start = end;
      return seg;
    });
    if (start < 360) segments.push(`#d1d5db ${start}deg 360deg`);
    return `conic-gradient(${segments.join(', ')})`;
  }

  private getGenderPercent(demographics: any, gender: string): number {
    const genders = demographics?.audience_genders;
    if (!Array.isArray(genders)) return 0;
    const match = genders.find((item: any) =>
      (item?.gender ?? '').toString().toLowerCase() === gender.toLowerCase()
    );
    const pct = Number(match?.percentage ?? 0);
    return Number.isFinite(pct) ? Math.min(1, Math.max(0, pct)) : 0;
  }

  private normalizePercent(value: unknown): number {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return 0;
    const normalized = n > 1 ? n / 100 : n;
    return Math.min(1, Math.max(0, normalized));
  }

  private formatFollowers(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm';
    if (n >= 1_000) return Math.round(n / 1_000) + 'k';
    return n.toString();
  }
}

